import CT from '@marketplace-ai/tags';
import { Link } from 'react-router';
import { useBrandConfig } from '~/config/branding';

const TAGS = CT.layout.footer;

const LEGAL_LINK_CLASS = 'hover:text-grey-100 transition-colors';

const LEGAL_LINKS = [
  { path: '/privacy', label: 'Privacy Policy' },
  { path: '/terms', label: 'Terms of Service' },
  { path: '/eula', label: 'EULA' },
] as const;

interface Props {
  compactOnMobile?: boolean;
  /**
   * Open the legal pages in a NEW TAB instead of navigating the app. [MCV-1723]
   * For the signed-in shells (VaultHeader, MarketHeader): every legal page is a
   * public path, ApplicationBoundary renders <PublicApp/> in place of the working
   * <App/> for public paths, so a same-tab link would unmount the app. The
   * signed-out surfaces keep the in-app router links.
   */
  legalLinksInNewTab?: boolean;
  /**
   * Point Contact at the brand's support@ inbox instead of the public info@ one. [MCV-1779]
   * Set by the signed-in shells (VaultHeader, MarketHeader): in-app traffic goes to support@,
   * landing and marketing traffic (signed-out footer, Contact page, legal pages) stays on info@.
   */
  signedIn?: boolean;
}

export const SiteFooter = ({
  compactOnMobile = false,
  legalLinksInNewTab = false,
  signedIn = false,
}: Props) => {
  const { siteName, linkedinUrl, contactEmail, supportEmail } = useBrandConfig();
  const contactAddress = signedIn ? supportEmail : contactEmail;

  return (
    <footer className="w-full bg-grey-900 border-t border-grey-900 mt-auto">
      <div
        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${compactOnMobile ? 'py-2 sm:py-3' : 'py-3'}`}
      >
        <div className="flex flex-col md:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-3">
            {/* [MCV-1477] LinkedIn icon linking to the brand's company page.
                heroicons has no brand glyph, so the mark is inlined; `fill-current`
                lets it inherit the footer's grey/hover treatment. */}
            <a
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${siteName} on LinkedIn`}
              data-cy={TAGS.linkedin}
              className="text-grey-500 hover:text-grey-100 transition-colors"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="size-5 fill-current">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
            <div className="flex flex-col items-center md:items-start text-xs text-grey-500">
              <p>
                &copy; {new Date().getFullYear()} Marketplace AI.
                <span className={compactOnMobile ? 'hidden sm:inline' : undefined}>
                  {' '}
                  All Rights Reserved.
                </span>
                <span className="hidden lg:inline">
                  {' '}
                  — {siteName}&reg; is a registered trademark of Marketplace AI.
                </span>
              </p>
              <p className={compactOnMobile ? 'hidden sm:block lg:hidden' : 'lg:hidden'}>
                {siteName}&reg; is a registered trademark of Marketplace AI.
              </p>
            </div>
          </div>

          {/* `relative` (new-tab links only) keeps their absolutely positioned sr-only
              text inside the footer instead of the document. */}
          <nav
            className={`${legalLinksInNewTab ? 'relative ' : ''}flex flex-wrap justify-center items-center ${compactOnMobile ? 'gap-x-2 sm:gap-x-4' : 'gap-x-4'} gap-y-1 text-xs text-grey-500 font-medium`}
          >
            {LEGAL_LINKS.map(({ path, label }) =>
              legalLinksInNewTab ? (
                <a
                  key={path}
                  href={path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={LEGAL_LINK_CLASS}
                >
                  {label}
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              ) : (
                <Link key={path} to={path} className={LEGAL_LINK_CLASS}>
                  {label}
                </Link>
              )
            )}
            <a
              href={`mailto:${contactAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-grey-100 transition-colors"
            >
              Contact
            </a>
          </nav>
        </div>
      </div>
    </footer>
  );
};
